import React, { FC, PropsWithChildren } from 'react'
import { makeStyles } from 'tss-react/mui'
import withV4IndexPriority from '../../../styles/withV4IndexPriority'
import { Theme } from '@mui/material/styles'
import { Grid } from '@mui/material'
import { StylesProps } from '../../../types/interface/styles.interface'
import { Typography } from '@dg-shared'
import { TestPropsType, TranslatedTextType } from '../../../types'
import { ThemeBrandPageBackgroundNameType } from 'BrandTheme/types'
import MobileHeaderText from 'components/MobileHeaderText'
import { getProductName } from '@dg-util'

export const useLMBgHeaderStyles = makeStyles<StylesProps>()((theme: Theme, props) =>
  withV4IndexPriority({
    container: {
      display: 'flex',
      flexWrap: 'nowrap',
      flexDirection: 'column',
      minHeight: `${props.height}px`,
      backgroundColor: theme.brand.colors.clean,
      ...theme.brand.assets.layout?.LMBgHeaderContainer,
    },

    page: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      padding: theme.spacing(2),
      ...theme.brand.assets.layout?.LMBgHeaderPage,
      [theme.breakpoints.down('md')]: {
        flex: 1,
        paddingTop: theme.spacing(1),
      },
    },

    header: {
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 0,
      padding: theme.spacing(2),
      minHeight: 200,
      justifyContent: 'space-between',
      borderRadius: '0 0 0 0',
      position: 'relative',
      ...theme.brand.assets.pageBackgrounds[props.brandPageBackground],
      ...theme.brand.assets.layout?.LMBgHeaderHeader,
    },

    logoWrapper: {
      display: 'flex',
      flex: 0,
      marginBottom: theme.spacing(2),
      [theme.breakpoints.down('lg')]: {
        ...theme.brand.assets.layout?.mobileCustomLogoWrapper,
      },
    },

    logoImage: {
      alignSelf: 'start',
      height: props.logoImageHeight ? props.logoImageHeight : '28px',
    },

    textFooter: {
      textAlign: 'center',
      padding: `${theme.spacing(1)} 0 0`,
      marginBottom: '0',
    },
  })
)

export type LMBgHeaderProps = {
  headerTitle?: TranslatedTextType
  headerClass?: string
  headerSubTitle?: TranslatedTextType
  textLogoAlt: TranslatedTextType
  textFooter?: TranslatedTextType
  headerContainerClass?: string
  containerClass?: string
  pageClass?: string
  imageGradient?: boolean
  imageLogo: string
  brandPageBackground: ThemeBrandPageBackgroundNameType
  testProps?: TestPropsType
}

const LMBgHeader: FC<PropsWithChildren<LMBgHeaderProps>> = ({
  headerTitle,
  headerSubTitle,
  headerClass = '',
  textFooter,
  headerContainerClass = '',
  containerClass = '',
  pageClass = '',
  textLogoAlt,
  imageLogo,
  children,
  imageGradient,
  brandPageBackground,
  testProps,
}) => {
  const { classes, cx } = useLMBgHeaderStyles({
    brandPageBackground,
    leftColGradientReverse: imageGradient,
    height: window.innerHeight,
    productName: getProductName(),
  })

  return (
    <Grid container className={cx(classes.container, containerClass)}>
      <div
        data-testid='mobile-header'
        {...testProps}
        className={cx(classes.header, headerContainerClass)}
      >
        <div className={classes.logoWrapper}>
          <img
            id={`mobile-header-img`}
            className={classes.logoImage}
            src={imageLogo}
            alt={`${textLogoAlt} Logo`}
          />
        </div>
      </div>
      <MobileHeaderText
        headerTitle={headerTitle}
        headerSubTitle={headerSubTitle}
        headerClass={headerClass}
      />
      <Grid item xs={12} className={cx(classes.page, pageClass)}>
        {children}
        {!!textFooter && (
          <Typography size='small' className={classes.textFooter} color='textDisclaimer'>
            {textFooter}
          </Typography>
        )}
      </Grid>
    </Grid>
  )
}

export default LMBgHeader
